import { Prescription } from './prescription.interface';

export interface GuaranteeApplicationSearchModel {
  page: number;
  pageSize: number;
  sortBy?: string;
  sortDirection?: string;
  userId?: string;
  patientName?: string;
  creatorName?: string;
  status?: GuaranteeApplicationStatus;
  id?: string;
  appointmentId?: string;
  creatorType?: GuaranteeCreatorType | GuaranteeCreatorTypeString;
  creatorUserId?: string;
  isLiloStaff?: boolean;
  creationDate?: string;
  prescriptions?: Prescription[];
  description?: string;
  diagnoses?: DiagnosisResponse[];
  prescriptionName?: string;
}

export interface GuaranteeApplication {
  id: string;
  patientId: string;
  patientName: string;
  creatorType: GuaranteeCreatorType | GuaranteeCreatorTypeString;
  creatorUserId: string;
  creatorName: string;
  userId: string;
  isLiloStaff: boolean;
  creationDate: string;
  lastUpdateDate: string;
  lastUpdateByUserId: string;
  status: GuaranteeApplicationStatus;
  prescriptions?: Prescription[];
  description?: string;
  diagnoses?: DiagnosisResponse[];
  diagnosesCoverage?: GuaranteeDiagnosisCoverage[];
  contractId: string;
  partnerId: string;
  provider: string;
}

export interface GuaranteeDiagnosisCoverage {
  icd10Code: string;
  icpc2Code: string;
  protocolId?: string;
  isCovered: boolean;
}

export interface DiagnosisResponse {
  icd10: {
    code: string;
    name: string;
  };
  icpc2: {
    code: string;
    name: string;
  };
  role: string;
}

export enum GuaranteeCreatorType {
  Doctor = 0,
  HealthManager = 1,
}

export enum GuaranteeCreatorTypeString {
  Doctor = 'Doctor',
  HealthManager = 'HealthManager',
}

export enum GuaranteeApplicationStatus {
  Auto = 'Auto',
  Processed = 'Processed',
  WaitProcessing = 'WaitProcessing',
}

export enum GuaranteeStatus {
  Auto = 'Auto',
  Guaranteed = 'Guaranteed',
  NotGuaranteed = 'NotGuaranteed',
  Unknown = 'Unknown',
}

export const APPROVE_REASON: string = "Проставлено автоматично після обробки в статус Гарантовано";
export const DECLINE_REASONS: string[] = [
  "Нестраховий діагноз.",
  "Ускладнення або наслідки нестрахового діагнозу.",
  "Діагностика нестрахового діагнозу після встановлення клінічного діагнозу.",
  "Метод лікування не погоджений Асистуючою компанією.",
  "Метод лікування не погоджений згідно з Переліком медичних послуг та медикаментів страхових захворювань.",
  "Профілактичний огляд / медкомісія без погіршення стану здоров’я.",
  "Нетрадиційна медицина.",
  "Непокривані препарати: фітопрепарати / гомеопатія / гепатопротектори / хондропротектори / пробіотики / гормональні / ферментні / моновітаміни.",
  "Експериментальне або науково-дослідне лікування.",
  "Послуги психотерапевта / логопеда / сурдолога / косметолога / сонолога / гомеопата / нарколога поза умовами договору.",
  "Протезування / ендопротезування / пластичні / реконструктивні операції / планове лікування менісків, зв’язок, посттравматичних кіст.",
  "Планові кардіохірургічні / судинні / нейрохірургічні втручання.",
  "Зміна ваги / лікування ожиріння / модифікація тіла / зміна статі.",
  "Лікування келоїдних рубців / видалення бородавок, папілом, родимок, ліпом, поліпів, гемангіом, невусів, доброякісних новоутворень шкіри без ускладнень.",
  "Штучне запліднення / аборт / планування сім’ї / контрацепція / стерилізація.",
  "Щеплення, не передбачені програмою.",
  "Реабілітація поза погодженими винятками.",
  "Санаторно-курортне / профілактичне лікування / оздоровчі послуги.",
  "Медична документація, не пов’язана зі страховим випадком.",
  "Вагітність та пологи поза умовами договору.",
  "Контрольна / уточнююча діагностика для визначення типу, стадії, форми захворювання або методу лікування.",
  "Лазерне лікування / корекція зору / лікування сітківки.",
  "Трансплантація / донорські витрати / кров та препарати крові / пересадка клітин / кісткового мозку.",
  "Незавершене до початку договору лікування / операції / планове видалення старих металоконструкцій.",
  "Непокривані спеціалізовані дослідження: понад 4 гормони / імунограма / алергопроби / алергопанель / онкомаркери / ДНК-діагностика / денситометрія / генетичні дослідження.",
  "Лікування симптомів / неуточнених станів до встановлення остаточного діагнозу.",
  "Підтримуюча / замісна / тривала гормональна терапія.",
  "Лікування понад 30 днів за одним випадком або понад 2 випадки за період дії договору без погодження.",
  "Лікування ІПСШ / ТОРЧ-інфекцій / папіломавірусної інфекції та інших виключених інфекцій.",
  "Самостійно придбані медикаменти / витратні матеріали поза аптечною мережею України або не зареєстровані в Україні.",
  "Медикаменти не погоджені Асистуючою компанією / медикаментозне забезпечення понад 30 днів / 2 і більше препарати однієї фармгрупи.",
  "Препарати з антигіпоксичною / антиоксидантною дією, препарати для покращення кровотоку, замінники синовіальної / слізної рідини.",
  "Стенти / шунти / протези / штучні зв’язки / електроди / набори для артроскопії та інші непокривані вироби.",
  "Милиці / післяопераційні бандажі.",
  "Лінзи / окуляри / слухові апарати / інгалятори / ортопедичні засоби.",
  "Засоби гігієни / косметичні засоби.",
  "Медичне обладнання / пристрої / інструменти / товари медичного призначення поза винятками.",
  "Подія пов’язана з алкогольним / наркотичним / токсичним сп’янінням.",
  "Подія сталася через форс-мажор / радіоактивне / хімічне / біологічне / токсичне забруднення / вибухові речовини.",
  "Подія сталася під час виконання бойових завдань / участі в обороні держави.",
  "Подія пов’язана з екстремальними видами спорту.",
  "Випадок не підпадає під визначення страхового / діагноз не входить до страхових ризиків.",
  "Лікування хронічного захворювання у стадії ремісії / компенсації / субкомпенсації / персистуючої інфекції.",
  "Лікування залишкових явищ / хронічного захворювання в ремісії / неповній ремісії / компенсації.",
  "Захворювання / травма / нещасний випадок виникли до початку дії договору.",
  "Медична допомога отримана до початку або після завершення дії договору.",
  "Медична допомога отримана в неоплачений період страхування.",
  "Подія сталася поза територією дії договору.",
  "Витрати пов’язані з благодійними внесками.",
  "Звернення щодо захворювання, за яким встановлена група інвалідності, або його наслідків.",
  "Випадок у особи з диспансерного обліку / онкологією / тяжкими декомпенсованими хворобами.",
  "Особливо небезпечні / небезпечні інфекційні та паразитарні хвороби, епідемії.",
  "Планова госпіталізація / планове оперативне втручання у перші 95 днів дії договору.",
  "Випадок не визнається страховим згідно з ЗУСП / договором.",
  "Випадок входить до Переліку нестрахових захворювань / послуги не погоджені Асистуючою компанією / застосовуються винятки та обмеження.",
  "Ушкодження / хвороба мали місце до укладення договору / є наслідком самолікування / лікування не було завершене до старту договору.",
  "Вперше виявлена хвороба, лікування якої не покривається — оплачується лише діагностика до встановлення діагнозу.",
  "Альтернативні консультації / дублювання обстежень / непогоджені медичні чи медикаментозні послуги.",
  "Благодійні внески не відшкодовуються.",
  "Моральна шкода / упущена вигода / судові витрати / інфляційні втрати не відшкодовуються.",
  "Інші підстави для відмови, передбачені договором і не заборонені законом.",
  "Виплата неможлива через санкції / заборони / економічні обмеження.",
];

export const EDIT_REASONS: string[] = [
  "Зміна призначення від ПЛ (Невірна назва/кількість послуг/ліків)",
  "Зміна призначення від суміжного фахівця (Невірна назва/кількість послуг/ліків)",
  "Помилкове призначення від ПЛ",
  "Помилкове призначення від суміжного фахівця",
  "Нові симптоми в межах діагнозу",
  "Підготовка перед обстеженням (додати ЛЗ)",
  "Помилкове створення послуги ХМом",
  "Дубль призначення",
  "Клієнт замовив ЛЗ самостійно (/виконав послугу/записався самостійно)",
  "Клієнт відмовився від послуги",
  "Створення нових послуг в гарантації, тудушки на які були протерміновані",
];
